/**
 * Casca da aplicação: conteúdo rolável e navegação fixa embaixo.
 *
 * Navegação inferior porque o app é usado em pé, na academia, com uma mão só —
 * o topo da tela de um celular grande está fora do alcance do polegar.
 *
 * **A aba ativa se distingue por três coisas**, não por uma: uma pastilha âmbar
 * atrás do ícone, o traço do ícone mais grosso, e o rótulo em negrito. A versão
 * anterior mudava só a cor do texto, o que sumia no sol e não chegava a quem
 * tem daltonismo. É a mesma regra do design system que vale para severidade:
 * cor, forma e palavra sempre juntas.
 */

import { useTranslation } from 'react-i18next';
import { NavLink, Outlet } from 'react-router-dom';

import { Brand } from '@/components/Brand';
import { LocalePicker } from '@/components/LocalePicker';
import { TabIcon, type TabName } from '@/components/TabIcon';

const TABS: ReadonlyArray<{ to: string; name: TabName; key: string }> = [
  { to: '/today', name: 'today', key: 'nav.today' },
  { to: '/plan', name: 'plan', key: 'nav.plan' },
  { to: '/messages', name: 'messages', key: 'nav.messages' },
  { to: '/profile', name: 'profile', key: 'nav.profile' },
];

export function AppShell() {
  const { t } = useTranslation(['common']);

  return (
    <div className="mh-shell">
      {/* Barra fina com a marca. Instalado como PWA não há barra de endereço,
          e sem isto o aplicativo fica sem nenhuma identificação na tela.
          Substituiu os botões que flutuavam sobre o conteúdo no canto. */}
      <header className="flex items-center justify-between border-b border-(--color-sand-200) bg-(--color-sand-0) px-5 py-3">
        <Brand compact />
        <LocalePicker />
      </header>

      <main className="flex-1 pb-28">
        <Outlet />
      </main>

      <nav
        aria-label={t('common:app.name')}
        className="fixed inset-x-0 bottom-0 mx-auto max-w-(--shell-max-width) border-t border-(--color-sand-200) bg-(--color-sand-0)"
        style={{ paddingBottom: 'var(--safe-bottom)' }}
      >
        <ul className="flex">
          {TABS.map((tab) => (
            <li key={tab.to} className="flex-1">
              <NavLink
                to={tab.to}
                className="flex min-h-(--tap-target) flex-col items-center gap-1 py-2"
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={[
                        'flex h-8 w-14 items-center justify-center rounded-full transition-colors',
                        isActive
                          ? 'bg-(--color-amber-100) text-(--color-amber-700)'
                          : 'text-(--color-sand-500)',
                      ].join(' ')}
                    >
                      <TabIcon name={tab.name} active={isActive} />
                    </span>
                    <span
                      className={[
                        'text-xs',
                        isActive
                          ? 'font-semibold text-(--color-amber-700)'
                          : 'font-medium text-(--color-sand-500)',
                      ].join(' ')}
                    >
                      {t(`common:${tab.key}`)}
                    </span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
